import React, {useState, useEffect} from 'react';
import {useLocation} from 'react-router-dom';

import {hooks} from '../hooks';
import {text} from '../text';
import {custom} from '../custom';
import {theme} from '../constants';
import {components} from '../components';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { MdSearch, MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';

const SearchIcon = MdSearch as any;
const CheckBoxIcon = MdCheckBox as any;
const CheckBoxOutlineBlankIcon = MdCheckBoxOutlineBlank as any;

export const CheckerTask: React.FC = () => {
  const {state} = useLocation();
  const navigate = hooks.useAppNavigate();
  const task = state?.task;
  const type = state?.type; // 'persiapan' | 'pemeriksaan' | 'inspeksi_mandiri'
  const { t } = useLanguage();
  const checkerTaskTrans = (t as any).checkerTask || {};

  const [availableCheckerChecklists, setAvailableCheckerChecklists] = useState<any[]>([]);
  const [selectedCheckerAttachmentIds, setSelectedCheckerAttachmentIds] = useState<number[]>([]);
  const [itemQty, setItemQty] = useState<Record<number, number>>({});
  const [itemSerial, setItemSerial] = useState<Record<number, string>>({});
  const [searchCheckerChecklist, setSearchCheckerChecklist] = useState<string>('');
  const [checkerNote, setCheckerNote] = useState(
    type === 'persiapan' ? (task?.prepare_note || '')
    : type === 'inspeksi_mandiri' ? (task?.self_check_note || '')
    : (task?.checker_note || '')
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddCheckerChecklistModal, setShowAddCheckerChecklistModal] = useState(false);
  const [newCheckerChecklistTitle, setNewCheckerChecklistTitle] = useState('');
  const [newCheckerChecklistCategory, setNewCheckerChecklistCategory] = useState('');

  useEffect(() => {
    if (!task) {
      navigate(-1);
      return;
    }

    if (type === 'persiapan') {
      if (task.checker_checklists) {
        setSelectedCheckerAttachmentIds(task.checker_checklists.map((a: any) => a.id));
        const qtyMap: Record<number, number> = {};
        const serialMap: Record<number, string> = {};
        task.checker_checklists.forEach((a: any) => {
          qtyMap[a.id] = a.qty ?? 1;
          serialMap[a.id] = a.serial_number || '';
        });
        setItemQty(qtyMap);
        setItemSerial(serialMap);
      }
      const fetchCheckerChecklists = async () => {
        try {
          const response = await axiosInstance.get('/tasks/checker-checklists');
          setAvailableCheckerChecklists(response.data);
        } catch (error) {
          console.error('Failed to fetch checker checklists:', error);
        }
      };
      fetchCheckerChecklists();
    } else if (type === 'inspeksi_mandiri') {
      setAvailableCheckerChecklists(task.checker_checklists || []);
      if (task.checker_checklists) {
        const checked = task.checker_checklists.filter((a: any) => a.self_is_checked);
        setSelectedCheckerAttachmentIds(checked.map((a: any) => a.id));
        const qtyMap: Record<number, number> = {};
        const serialMap: Record<number, string> = {};
        task.checker_checklists.forEach((a: any) => {
          qtyMap[a.id] = a.qty ?? 1;
          serialMap[a.id] = a.serial_number || '';
        });
        setItemQty(qtyMap);
        setItemSerial(serialMap);
      }
    } else {
      // 'pemeriksaan' (checker). Kalau checker belum pernah submit pemeriksaan sama sekali
      // (checker_status masih 'prepared'), mulai dari hasil inspeksi mandiri assignee sebagai
      // acuan — tugas checker adalah memverifikasi fisik terhadap klaim assignee, bukan mulai
      // dari kosong. Kalau checker sudah pernah submit (mode edit ulang), pakai hasil checker
      // sendiri sebelumnya.
      setAvailableCheckerChecklists(task.checker_checklists || []);
      if (task.checker_checklists) {
        const hasCheckerSubmitted = task.checker_status === 'checked' || task.checker_status === 'pending';
        const checked = task.checker_checklists.filter((a: any) => hasCheckerSubmitted ? a.is_checked : a.self_is_checked);
        setSelectedCheckerAttachmentIds(checked.map((a: any) => a.id));
        const qtyMap: Record<number, number> = {};
        const serialMap: Record<number, string> = {};
        task.checker_checklists.forEach((a: any) => {
          qtyMap[a.id] = a.qty ?? 1;
          serialMap[a.id] = a.serial_number || '';
        });
        setItemQty(qtyMap);
        setItemSerial(serialMap);
      }
    }
  }, [task, navigate, type]);

  const handleSave = async (isSubmit: boolean) => {
    try {
      if (isSubmit) setIsSubmitting(true);
      else setIsLoading(true);

      await axiosInstance.post(`/tasks/${task.id}/checker-checklists`, {
        type: type,
        checker_checklist_ids: selectedCheckerAttachmentIds,
        checker_items: selectedCheckerAttachmentIds.map(id => ({
          id,
          qty: itemQty[id] ?? 1,
          serial_number: itemSerial[id] || null,
        })),
        note: checkerNote,
        is_submit: isSubmit
      });

      navigate(-1);
    } catch (error) {
      console.error('Failed to save checker checklists:', error);
      alert((checkerTaskTrans.failedToSave || 'Gagal menyimpan ') + type);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleAddCheckerChecklist = async () => {
    if (!newCheckerChecklistTitle.trim()) return;
    try {
      const response = await axiosInstance.post('/tasks/checker-checklists', {
        title: newCheckerChecklistTitle,
        category: newCheckerChecklistCategory || null,
      });
      setAvailableCheckerChecklists(prev => [...prev, response.data].sort((a: any, b: any) => a.title.localeCompare(b.title)));
      setNewCheckerChecklistTitle('');
      setNewCheckerChecklistCategory('');
      setShowAddCheckerChecklistModal(false);
    } catch (error) {
      console.error('Failed to create checker checklist:', error);
      alert(t.common?.error || 'Gagal menambah checklist pemeriksa');
    }
  };

  const renderHeader = () => (
    <components.Header title={
      type === 'persiapan' ? (checkerTaskTrans.preparationTitle || 'Persiapan Pemeriksaan')
      : type === 'inspeksi_mandiri' ? (checkerTaskTrans.selfInspectionTitle || 'Inspeksi Mandiri')
      : (checkerTaskTrans.inspectionTitle || 'Pemeriksaan Tugas')
    } goBack={true} />
  );

  const renderAddCheckerChecklistModal = () => {
    if (!showAddCheckerChecklistModal) return null;
    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 320,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column'
          }}
        >
          <text.H4 style={{ marginBottom: 16 }}>{checkerTaskTrans.addChecklistTitle || 'Tambah Checklist Pemeriksa'}</text.H4>
          <input
            type="text"
            placeholder={checkerTaskTrans.addChecklistPlaceholder || "Contoh: Helm Safety, Kunci Inggris..."}
            value={newCheckerChecklistTitle}
            onChange={(e) => setNewCheckerChecklistTitle(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: '1px solid #E5E7EB', fontSize: 16, outline: 'none',
              marginBottom: 12, boxSizing: 'border-box', fontFamily: 'inherit'
            }}
          />
          <text.T14 style={{ color: '#6B7280', marginBottom: 6 }}>
            {checkerTaskTrans.addChecklistCategoryLabel || 'Kategori'}
          </text.T14>
          <select
            value={newCheckerChecklistCategory}
            onChange={(e) => setNewCheckerChecklistCategory(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: '1px solid #E5E7EB', fontSize: 16, outline: 'none',
              marginBottom: 20, boxSizing: 'border-box', fontFamily: 'inherit',
              backgroundColor: '#fff', color: theme.colors.mainDark,
            }}
          >
            <option value="">{checkerTaskTrans.addChecklistCategoryPlaceholder || 'Tanpa kategori'}</option>
            <option value="tools">{checkerTaskTrans.categoryTools || 'Tools'}</option>
            <option value="installation items">{checkerTaskTrans.categoryInstallationItems || 'Installation Items'}</option>
            <option value="consumables">{checkerTaskTrans.categoryConsumables || 'Consumables'}</option>
          </select>
          <div style={{ display: 'flex', gap: 10 }}>
            <components.Button title={t.common?.cancel || 'Batal'} style={{ backgroundColor: '#F3F4F6', color: '#4B5563', padding: '10px' }} containerStyle={{ flex: 1 }} onClick={() => setShowAddCheckerChecklistModal(false)} />
            <components.Button title={t.common?.save || 'Simpan'} style={{ padding: '10px' }} containerStyle={{ flex: 1 }} onClick={handleAddCheckerChecklist} />
          </div>
        </div>
      </div>
    );
  };

  const showSelfCheckReference = type !== 'persiapan' && type !== 'inspeksi_mandiri' && task?.checker_status === 'prepared';

  const renderContent = () => (
    <div className='container' style={{paddingTop: 80, paddingBottom: 100}}>
      {showSelfCheckReference && (
        <div style={{
          backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10,
          padding: '10px 14px', marginBottom: 16,
        }}>
          <text.T14 style={{color: '#1D4ED8'}}>
            {checkerTaskTrans.selfCheckReferenceNote || 'Checklist di bawah sudah diisi otomatis sesuai hasil inspeksi mandiri pekerja. Sesuaikan dengan kondisi fisik yang Anda lihat langsung.'}
          </text.T14>
        </div>
      )}
      <div style={{marginBottom: 20}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
          <text.H5>{checkerTaskTrans.checkerChecklist || 'Checklist Pemeriksa'}</text.H5>
          {type === 'persiapan' && (
            <div onClick={() => setShowAddCheckerChecklistModal(true)} style={{ cursor: 'pointer' }}>
              <text.T14 style={{ color: theme.colors.mainColor, fontWeight: '600' }}>+ {(t.marketingActivity as any)?.add || 'Tambah'}</text.T14>
            </div>
          )}
        </div>
        <custom.InputField
          placeholder={checkerTaskTrans.searchChecklist || "Cari checklist pemeriksa..."}
          value={searchCheckerChecklist}
          onChange={(e: any) => setSearchCheckerChecklist(e.target.value)}
          leftIcon={<SearchIcon size={20} color="#9CA3AF" />}
          containerStyle={{marginBottom: 10}}
        />
        {(() => {
          const CAT_ORDER = ['tools', 'installation items', 'consumables'];
          const CAT_LABELS: Record<string, string> = { 'tools': 'Tools', 'installation items': 'Installation Items', 'consumables': 'Consumables' };
          const CAT_COLORS: Record<string, {bg: string, color: string, border: string}> = {
            'tools':              { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
            'installation items': { bg: '#F0FDF4', color: '#15803D', border: '#86EFAC' },
            'consumables':        { bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
          };
          const filteredList = availableCheckerChecklists
            .filter(att => att.title.toLowerCase().includes(searchCheckerChecklist.toLowerCase()));
          const activeCats = CAT_ORDER;
          const groups: { category: string | null; items: typeof filteredList }[] = [];
          activeCats.forEach(cat => {
            const items = filteredList.filter(a => a.category === cat);
            if (items.length > 0) groups.push({ category: cat, items });
          });
          const uncategorized = filteredList.filter(a => !a.category || !CAT_ORDER.includes(a.category));
          if (uncategorized.length > 0) groups.push({ category: null, items: uncategorized });
          return (
            <div className="scrollbar-visible" style={{ border: '1px solid #E5E7EB', borderRadius: 10, backgroundColor: '#FFFFFF', maxHeight: 300, overflowY: 'auto', padding: 8 }}>
              {groups.length === 0 && (
                <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                  <text.T14 style={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                    {availableCheckerChecklists.length === 0
                      ? (checkerTaskTrans.noChecklistSetup || 'Data checklist belum ada.')
                      : ((t.common as any)?.noResults || 'Tidak ada hasil yang cocok.')}
                  </text.T14>
                </div>
              )}
              {groups.map((group, gIdx) => (
                <div key={group.category ?? '__none'}>
                  {group.category && (
                    <div style={{
                      padding: '5px 10px',
                      backgroundColor: CAT_COLORS[group.category]?.bg || '#F9FAFB',
                      borderBottom: `1px solid ${CAT_COLORS[group.category]?.border || '#E5E7EB'}`,
                      ...(gIdx > 0 ? { borderTop: '1px solid #E5E7EB' } : {}),
                    }}>
                      <text.T14 style={{ color: CAT_COLORS[group.category]?.color || '#374151', fontWeight: '700', fontSize: 11, textTransform: 'uppercase' as any, letterSpacing: 0.5 }}>
                        {CAT_LABELS[group.category] || group.category}
                      </text.T14>
                    </div>
                  )}
                  {group.items.map((att, iIdx) => {
                    const isSelected = selectedCheckerAttachmentIds.includes(att.id);
                    return (
                      <div
                        key={att.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedCheckerAttachmentIds(prev => prev.filter(item => item !== att.id));
                          } else {
                            setSelectedCheckerAttachmentIds(prev => [...prev, att.id]);
                            setItemQty(prev => ({ ...prev, [att.id]: prev[att.id] ?? 1 }));
                          }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                          backgroundColor: isSelected ? '#F0FDF4' : '#FFF', cursor: 'pointer',
                          borderBottom: iIdx < group.items.length - 1 ? '1px solid #F3F4F6' : 'none',
                        }}
                      >
                        {isSelected ? <CheckBoxIcon size={22} color={theme.colors.mainColor} /> : <CheckBoxOutlineBlankIcon size={22} color="#9CA3AF" />}
                        <text.T14 style={{ color: isSelected ? theme.colors.mainDark : '#4B5563', flex: 1 }}>{att.title}</text.T14>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Preview item yang sudah diceklis */}
      {selectedCheckerAttachmentIds.length > 0 && (() => {
        const selectedItems = availableCheckerChecklists.filter(a => selectedCheckerAttachmentIds.includes(a.id));
        if (selectedItems.length === 0) return null;
        return (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <text.H5 style={{ margin: 0 }}>{checkerTaskTrans.selectedItems || 'Item yang Diceklis'}</text.H5>
              <div style={{
                backgroundColor: theme.colors.mainColor, color: '#fff',
                borderRadius: 99, minWidth: 22, height: 22,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: '700', padding: '0 7px'
              }}>
                {selectedItems.length}
              </div>
            </div>
            {(() => {
              const CAT_ORDER = ['tools', 'installation items', 'consumables'];
              const CAT_LABELS: Record<string, string> = { 'tools': 'Tools', 'installation items': 'Installation Items', 'consumables': 'Consumables' };
              const CAT_COLORS: Record<string, {color: string}> = {
                'tools': { color: '#1D4ED8' },
                'installation items': { color: '#15803D' },
                'consumables': { color: '#C2410C' },
              };
              const previewGroups: { category: string | null; items: typeof selectedItems }[] = [];
              CAT_ORDER.forEach(cat => {
                const items = selectedItems.filter(i => i.category === cat);
                if (items.length > 0) previewGroups.push({ category: cat, items });
              });
              const uncategorized = selectedItems.filter(i => !i.category || !CAT_ORDER.includes(i.category));
              if (uncategorized.length > 0) previewGroups.push({ category: null, items: uncategorized });
              return (
                <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {previewGroups.map((group, gIdx) => (
                    <div key={group.category ?? '__none'}>
                      {group.category && (
                        <div style={{ marginBottom: 4, paddingLeft: 2 }}>
                          <text.T14 style={{ fontSize: 11, fontWeight: '700', textTransform: 'uppercase' as any, letterSpacing: 0.5, color: CAT_COLORS[group.category]?.color || '#374151' }}>
                            {CAT_LABELS[group.category] || group.category}
                          </text.T14>
                        </div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {group.items.map((item) => {
                          const qty = itemQty[item.id] ?? 1;
                          const isInstallation = item.category === 'installation items';
                          // Selisih dgn qty saat persiapan (inspeksi mandiri & pemeriksaan) — hanya utk
                          // tools: installation items & consumables memang wajar berkurang (terpasang/terpakai)
                          const preparedQty = type !== 'persiapan' && item.category === 'tools' && item.prepared_qty != null ? Number(item.prepared_qty) : null;
                          const qtyDiff = preparedQty != null ? qty - preparedQty : 0;
                          return (
                            <div key={item.id} style={{ backgroundColor: '#fff', borderRadius: 8, border: `1px solid ${qtyDiff < 0 ? '#FCA5A5' : qtyDiff > 0 ? '#FCD34D' : '#BBF7D0'}`, padding: '8px 10px' }}>
                              {/* Tanpa ikon checkbox: daftar ini hanya ringkasan item terpilih (tidak bisa diklik) */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <text.T14 style={{ color: '#166534', flex: 1, fontWeight: '500' }}>{item.title}</text.T14>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                                  <button
                                    onClick={() => setItemQty(prev => ({ ...prev, [item.id]: Math.max(1, (prev[item.id] ?? 1) - 1) }))}
                                    style={{ width: 28, height: 28, borderRadius: '6px 0 0 6px', border: '1px solid #D1FAE5', borderRight: 'none', background: '#E7F9EF', color: theme.colors.mainDark, fontSize: 18, fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                                  >−</button>
                                  <input
                                    type="number" min={1} value={qty}
                                    onChange={e => { const v = parseInt(e.target.value, 10); setItemQty(prev => ({ ...prev, [item.id]: isNaN(v) || v < 1 ? 1 : v })); }}
                                    style={{ width: 40, height: 28, textAlign: 'center', border: '1px solid #D1FAE5', borderLeft: 'none', borderRight: 'none', fontSize: 13, fontWeight: '600', color: '#166534', outline: 'none', fontFamily: 'inherit', MozAppearance: 'textfield' as any }}
                                  />
                                  <button
                                    onClick={() => setItemQty(prev => ({ ...prev, [item.id]: (prev[item.id] ?? 1) + 1 }))}
                                    style={{ width: 28, height: 28, borderRadius: '0 6px 6px 0', border: '1px solid #D1FAE5', borderLeft: 'none', background: '#E7F9EF', color: theme.colors.mainDark, fontSize: 18, fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                                  >+</button>
                                </div>
                              </div>
                              {qtyDiff !== 0 && (
                                <text.T14 style={{ marginTop: 6, fontSize: 12, lineHeight: 1.4, color: qtyDiff < 0 ? '#DC2626' : '#B45309' }}>
                                  {(qtyDiff < 0
                                    ? (checkerTaskTrans.qtyLessThanPrepared || 'Kurang {diff} dari jumlah saat persiapan ({prepared}).')
                                    : (checkerTaskTrans.qtyMoreThanPrepared || 'Lebih {diff} dari jumlah saat persiapan ({prepared}).'))
                                    .replace('{diff}', String(Math.abs(qtyDiff))).replace('{prepared}', String(preparedQty))}
                                </text.T14>
                              )}
                              {isInstallation && (
                                <div style={{ marginTop: 6 }}>
                                  <input
                                    type="text" placeholder={checkerTaskTrans.serialPlaceholder || 'Serial number (pisahkan dengan koma)'}
                                    value={itemSerial[item.id] || ''}
                                    onChange={e => setItemSerial(prev => ({ ...prev, [item.id]: e.target.value }))}
                                    style={{ width: '100%', padding: '5px 10px', borderRadius: 6, border: '1px solid #D1FAE5', fontSize: 13, color: '#166534', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', backgroundColor: '#F0FDF4' }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {gIdx < previewGroups.length - 1 && (
                        <div style={{ height: 1, backgroundColor: '#BBF7D0', marginTop: 8 }} />
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        );
      })()}

      <div style={{marginBottom: 20}}>
        <text.H5 style={{marginBottom: 10}}>
          {type === 'persiapan' ? (checkerTaskTrans.preparationNotesTitle || 'Catatan Persiapan (Notes)')
          : type === 'inspeksi_mandiri' ? (checkerTaskTrans.selfInspectionNotesTitle || 'Catatan Inspeksi Mandiri (Notes)')
          : (checkerTaskTrans.inspectionNotesTitle || 'Catatan Pemeriksaan (Notes)')}
        </text.H5>
        <textarea
          value={checkerNote}
          onChange={(e) => setCheckerNote(e.target.value)}
          placeholder={
            type === 'persiapan' ? (checkerTaskTrans.addPreparationNotes || "Tambahkan catatan persiapan...")
            : type === 'inspeksi_mandiri' ? (checkerTaskTrans.addSelfInspectionNotes || "Tambahkan catatan inspeksi mandiri...")
            : (checkerTaskTrans.addInspectionNotes || "Tambahkan catatan pemeriksaan...")
          }
          style={{
            width: '100%', height: 120, padding: 14, borderRadius: 10,
            border: '1px solid #E5E7EB', fontFamily: 'inherit', fontSize: 16,
            resize: 'none', boxSizing: 'border-box'
          }}
        />
      </div>
    </div>
  );

  const renderButtons = () => (
    <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        backgroundColor: 'white', padding: '16px 24px 32px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
        zIndex: 10
    }}>
        <div className='container' style={{padding: 0, maxWidth: 650, margin: '0 auto', display: 'flex', flexDirection: 'row', gap: 12}}>
            <components.Button 
              title={isLoading ? (t.common?.saving || 'Menyimpan...') : (
                type === 'persiapan' ? (checkerTaskTrans.savePreparation || 'Simpan (Persiapan)')
                : type === 'inspeksi_mandiri' ? (checkerTaskTrans.saveSelfInspection || 'Simpan (Inspeksi Mandiri)')
                : (checkerTaskTrans.saveInspection || 'Simpan (Pemeriksaan)')
              )}
              colorScheme="light"
              onClick={() => { if (!isLoading && !isSubmitting) handleSave(false); }}
              containerStyle={{flex: 1, opacity: isLoading || isSubmitting ? 0.5 : 1}}
            />
            <components.Button 
              title={isSubmitting ? (t.common?.submitting || 'Submit...') : (
                type === 'persiapan' ? (checkerTaskTrans.submitPreparation || 'Submit (Persiapan)')
                : type === 'inspeksi_mandiri' ? (checkerTaskTrans.submitSelfInspection || 'Submit (Inspeksi Mandiri)')
                : (checkerTaskTrans.submitInspection || 'Submit (Pemeriksaan)')
              )} 
              onClick={() => { if (!isLoading && !isSubmitting) handleSave(true); }}
              containerStyle={{flex: 1, opacity: isLoading || isSubmitting ? 0.5 : 1}}
            />
        </div>
    </div>
  );

  return (
    <div id='screen' style={{backgroundColor: '#FFFFFF', minHeight: '100vh'}}>
      {renderHeader()}
      {renderContent()}
      {renderAddCheckerChecklistModal()}
      {renderButtons()}
    </div>
  );
};